import { useShellCreateAction } from '@ordo/ui/app-shell'
import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { ServerDataGrid } from 'compositions/DataGrid'
import { Page } from 'compositions/Page'
import { ShowInactiveToggle } from 'compositions/ShowInactiveToggle'
import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import type { Column } from 'types/data-grid'
import type { Template } from 'types/templates'

const COLUMNS: Column<Template>[] = [
  {
    key: 'name',
    header: 'Name',
    sortable: true,
    filterable: true,
    render: (row) => (
      <Link to={`/templates/${row.id}`} className="text-foreground hover:underline">
        {row.name}
        {!row.active && (
          <Badge variant="outline" className="ml-2 text-xs">
            Inactive
          </Badge>
        )}
      </Link>
    ),
  },
  {
    key: 'updatedAt',
    header: 'Last Modified',
    width: '30%',
    sortable: true,
    render: (row) => new Date(row.updatedAt).toLocaleDateString(),
  },
]

export function TemplateList() {
  const navigate = useNavigate()
  // On phones "New Template" is the shell's floating + button; the header button is desktop only.
  useShellCreateAction({ label: 'New Template', onCreate: () => navigate('/templates/new') })
  const [includeInactive, setIncludeInactive] = useState(false)
  const params = useMemo(() => ({ includeInactive }), [includeInactive])

  return (
    <Page>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Templates</h1>
        <div className="flex items-center gap-4">
          <ShowInactiveToggle checked={includeInactive} onCheckedChange={setIncludeInactive} />
          <Button asChild className="hidden md:inline-flex">
            <Link to="/templates/new">
              <Plus className="h-4 w-4" />
              New Template
            </Link>
          </Button>
        </div>
      </div>
      <ServerDataGrid<Template>
        endpoint="/api/templates"
        params={params}
        columns={COLUMNS}
        keyField="id"
        storageKey="templates"
        searchPlaceholder="Search templates…"
      />
    </Page>
  )
}
